import { getAudioDurationSeconds, getVideoDuration } from './fileUtil';

// 限制文件大小
export const checkFileSize = (
  fileList: FileList,
  { max, min }: { max?: number; min?: number },
) => {
  const result = Array.from(fileList).every((file: File) => {
    const fileSize = file.size;
    const isWithinMax = max ? fileSize <= max : true;
    const isWithinMin = min ? fileSize >= min : true;
    return isWithinMax && isWithinMin;
  });

  return result;
};

const resolutionMap: Record<string, { long: number; short: number }> = {
  '480p': { long: 854, short: 480 },
  '720p': { long: 1280, short: 720 },
  '1080p': { long: 1920, short: 1080 },
  '2k': { long: 2560, short: 1440 },
  '4k': { long: 3840, short: 2160 },
  '8k': { long: 7680, short: 4320 },
};

interface ResolutionLimit {
  min?: {
    long?: number;
    short?: number;
  };
  max?: {
    long?: number;
    short?: number;
  };
}

export const checkImageResolutionRange = async (
  fileList: FileList,
  limit: ResolutionLimit = {},
) => {
  const { min, max } = limit;
  let result = true;

  for (const file of fileList) {
    try {
      const res = await getImageResolutionFromFile(file);
      const maxSide = Math.max(res.width, res.height);
      const minSide = Math.min(res.width, res.height);

      if (min) {
        if ((min.long && maxSide < min.long) || (min.short && minSide < min.short)) {
          result = false;
          break;
        }
      }

      if (max) {
        if ((max.long && maxSide > max.long) || (max.short && minSide > max.short)) {
          result = false;
          break;
        }
      }
    } catch {
      result = false;
      break;
    }
  }

  return result;
};

export const checkSingleImageResolution = async (
  file: File,
  limit: ResolutionLimit = {},
) => {
  const { min, max } = limit;

  try {
    const res = await getImageResolutionFromFile(file);
    const maxSide = Math.max(res.width, res.height);
    const minSide = Math.min(res.width, res.height);

    if (min) {
      if ((min.long && maxSide < min.long) || (min.short && minSide < min.short)) {
        return false;
      }
    }

    if (max) {
      if ((max.long && maxSide > max.long) || (max.short && minSide > max.short)) {
        return false;
      }
    }

    return true;
  } catch {
    return false;
  }
};

export const checkSingleFileSize = (
  file: File,
  { max, min }: { max?: number; min?: number },
) => {
  const fileSize = file.size;
  const isWithinMax = max ? fileSize <= max : true;
  const isWithinMin = min ? fileSize >= min : true;
  return isWithinMax && isWithinMin;
};

// 限制文件类型
export const checkFileType = (fileList: FileList, accept: string[]) => {
  let result = Array.from(fileList).some((file: File) => accept.includes(file.type));
  return result;
};

// 限制文件时长
export const checkFileDuration = async (
  fileList: FileList,
  { min, max }: { min?: number; max?: number },
) => {
  let isValid = true;
  let duration: number = 0;
  for (const file of fileList) {
    try {
      // const duration: number = await getVideoDuration(file);
      if (
        file.type.startsWith('audio/') ||
        file.name.endsWith('.m4a') ||
        file.name.endsWith('.mp3') ||
        file.name.endsWith('.wav')
      ) {
        duration = (await getAudioDurationSeconds(file)) * 1000; // 单位：秒
      } else {
        duration = await getVideoDuration(file); // 单位：秒
      }

      if (min) {
        if (duration / 1000 < min) {
          isValid = false; // 发现不符合条件的文件，设置 isValid 为 false
          break; // 终止循环
        }
      }
      if (max) {
        if (duration / 1000 > max) {
          isValid = false; // 发现不符合条件的文件，设置 isValid 为 false
          break; // 终止循环
        }
      }
    } catch (error) {
      isValid = false;
    }
  }
  return { isValid, duration };
};

// 过滤出符合校验规则的文件
export const filterValidRuleFiles = async (
  fileList: FileList,
  options: {
    accept?: string[];
    duration?: { min?: number; max?: number };
    size?: { min?: number; max?: number };
  },
) => {
  const validFiles: File[] = [];
  const files = Array.from(fileList);

  for (const file of files) {
    let isValid = true;

    // 检查文件类型
    if (options.accept) {
      const typeValid = options.accept.includes(file.type);
      if (!typeValid) {
        isValid = false;
      }
    }

    // 检查文件大小
    if (isValid && options.size) {
      const sizeValid = checkFileSize([file] as unknown as FileList, options.size);
      if (!sizeValid) {
        isValid = false;
      }
    }

    // 检查文件时长
    if (isValid && options.duration) {
      const { isValid: durationValid } = await checkFileDuration(
        [file] as unknown as FileList,
        options.duration,
      );
      if (!durationValid) {
        isValid = false;
      }
    }

    if (isValid) {
      validFiles.push(file);
    }
  }

  return validFiles;
};

/**
 * 获取本地 File 图片的分辨率
 * @param {File} file - input[type="file"] 选中的图片文件
 * @returns {Promise<{width: number, height: number}>}
 */
export function getImageResolutionFromFile(
  file: File,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    if (!(file instanceof File)) {
      reject(new Error('入参必须是 File 对象'));
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      if (!e.target) {
        reject(new Error('FileReader event target is null'));
        return;
      }
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      };
      img.onerror = (err) => reject(err);
      img.src = e.target.result as string;
    };

    reader.onerror = (err) => reject(err);

    reader.readAsDataURL(file);
  });
}

export const checkImageResolution = (
  file: File,
  { min, max }: { min?: number; max?: number },
): Promise<{ valid: boolean; width: number; height: number }> => {
  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.src = objectUrl;
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const { width, height } = img;
      const isWithinMax = max ? width <= max && height <= max : true;
      const isWithinMin = min ? width >= min && height >= min : true;
      resolve({ valid: isWithinMax && isWithinMin, width, height });
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ valid: false, width: 0, height: 0 });
    };
  });
};
